/**
 * Hooks de operaciones administrativas — solo rol admin.
 *
 *  - Clientes: listar + crear
 *  - Cuentas conectadas: crear (la lista vive en useAccounts)
 *  - Freno maestro global
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiGet, apiPatch, apiPost } from "@/lib/api";
import type { ClientSlug, Platform } from "@/types";

export type ClientType = "pharma" | "consumer" | "b2b" | "internal";

export interface AdminClient {
  slug: string;
  name: string;
  country: string | null;
  plan: string | null;
  type: ClientType;
  active: boolean;
  onboardedAt: string;
  updatedAt: string;
  anthropicKeySet: boolean;
  anthropicKeySuffix: string | null;
}

export function useAdminClients() {
  return useQuery<AdminClient[]>({
    queryKey: ["admin", "clients"],
    queryFn: async () => {
      const res = await apiGet<{ items: AdminClient[] }>("/admin/clients");
      return res.items;
    },
  });
}

interface CreateClientInput {
  slug: string;
  name: string;
  country?: string;
  plan?: string;
  type: ClientType;
}

interface SetAnthropicKeyInput {
  slug: string;
  apiKey: string; // vacío = limpiar y volver al fallback global
}

export function useSetClientAnthropicKey() {
  const qc = useQueryClient();
  return useMutation<
    { ok: boolean; keySet: boolean; keySuffix: string | null },
    Error,
    SetAnthropicKeyInput
  >({
    mutationFn: async ({ slug, apiKey }) =>
      apiPatch<{ ok: boolean; keySet: boolean; keySuffix: string | null }>(
        `/admin/clients/${slug}/anthropic-key`,
        { apiKey },
      ),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["admin", "clients"] });
      toast.success(
        res.keySet
          ? `Key actualizada (…${res.keySuffix})`
          : "Key limpiada — usa la global",
      );
    },
    onError: (err) => toast.error(err.message),
  });
}

export function useCreateClient() {
  const qc = useQueryClient();
  return useMutation<AdminClient, Error, CreateClientInput>({
    mutationFn: async (input) => {
      const res = await apiPost<{ ok: boolean; client: AdminClient }>(
        "/admin/clients",
        input,
      );
      return res.client;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "clients"] });
      toast.success("Cliente creado");
    },
    onError: (err) => toast.error(err.message),
  });
}

interface CreateAccountInput {
  clientSlug: ClientSlug;
  platform: Platform;
  zernioProfileId: string;
  zernioAccountId: string;
  handle?: string;
  industry?: string;
  locale?: string;
  metaPageId?: string;
  tokenMode?: "champe" | "client";
}

export function useCreateCmAccount() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, CreateAccountInput>({
    mutationFn: async (input) =>
      apiPost<{ ok: boolean }>("/admin/cm-accounts", input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["accounts"] });
      toast.success("Cuenta conectada (con moderación apagada por seguridad)");
    },
    onError: (err) => toast.error(err.message),
  });
}

export type MasterKillLevel =
  | "none"
  | "bot_only"
  | "bot_and_escalations"
  | "full";

export interface MasterKillStatus {
  level: MasterKillLevel;
  updatedAt: string | null;
}

export function useMasterKillSwitch() {
  return useQuery<MasterKillStatus>({
    queryKey: ["admin", "master-kill"],
    refetchInterval: 60_000,
    queryFn: async () => apiGet<MasterKillStatus>("/admin/master-kill-switch"),
  });
}

interface SetMasterKillInput {
  level: MasterKillLevel;
  reason: string;
}

export function useSetMasterKillSwitch() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, SetMasterKillInput>({
    mutationFn: async (input) =>
      apiPost<{ ok: boolean }>("/admin/master-kill-switch", input),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["admin", "master-kill"] });
      qc.invalidateQueries({ queryKey: ["alerts"] });
      toast.success(
        vars.level === "none"
          ? "Freno maestro desactivado"
          : `Freno maestro aplicado: ${vars.level}`,
      );
    },
    onError: (err) => toast.error(err.message),
  });
}
